import type { Transition } from "@uirouter/react";
import _ from "lodash";
import queryString from "query-string";

import { setCountryFilterData } from "app/action_types/country_filter_action-types";
import { apiHelper } from "app/common/services/apiHelper";
import { AppInfoService } from "app/common/services/appInfoService";
import { chosenItems } from "app/common/services/chosenItems";
import { chosenSites } from "app/common/services/chosenSites";
import { i18nFilter } from "app/filters/i18nFilter";
import { loadCustomSegmentsMetadata } from "app/pages/segments/config/segmentsConfigHelpers";
import { AppsResourceService } from "app/services/AppsResourceService";
import { loadCustomIndustriesMetadata } from "app/services/category/userCustomCategoryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { keywordGroupsQueryClient, keywordGroupsTransforms } from "app/services/keyword-groups";
import { fetchSiteInfoQuery } from "app/services/sitesService/queries/sitesQueries";
import { SwRedux } from "app/services/SwRedux";

function dashboardResolver() {
    return async () => {
        await keywordGroupsQueryClient.prefetchSharedGroups();
        await keywordGroupsQueryClient.prefetchUserGroups();
    };
}

function siteInfo() {
    return (transition: Transition) => {
        const { key, isWWW, from: fromParams } = transition.params();
        if (!key) {
            return null;
        }

        const keyChanged = fromParams?.key !== key;
        // If key hasn't changed, return cached data immediately without API call
        if (!keyChanged && fromParams?.key) {
            return chosenSites.sitelistForLegend();
        }

        return fetchSiteInfoQuery({ keys: key, mainDomainOnly: !!isWWW }).then((headerData) => {
            chosenSites.updateMainSite(headerData);
            chosenSites.updateInfo(headerData);
            return chosenSites.sitelistForLegend();
        });
    };
}

const appInfoResolver = async (transition: Transition) => {
    let params = apiHelper.transformParamsForAPI(transition.params());
    params = _.pick(params, ["appId", "store"]);
    const data = await AppInfoService.getInfo(params.appId, params.store);
    const apps = params.appId.split(",");
    const mainAppId = apps.shift();
    const mainApp = data[mainAppId] || null;
    const tail: any[] = [];
    if (chosenItems.$first().$set) {
        chosenItems.$first().$set(mainApp);
    }
    apps.forEach((appId: any) => {
        tail.push(data[appId]);
    });
    chosenItems.$tail(tail);
    // because similarApps API only works with one app ID
    params.appId = mainAppId;
    const similarAppsData = await AppsResourceService.similarApps(params);
    chosenItems.similarApps = similarAppsData.slice(0, 5);
    document.title = mainApp != null ? i18nFilter()("titleTag.mobileapps.analysis", { app: mainApp.Title }) : "";

    return data;
};

const topCountriesResolver = async (transition: Transition) => {
    const redux = SwRedux.getInstance();
    const params = { ...transition.params() };
    const paramsChanges = { ...transition.paramsChanged() };
    const countryFilterRedux = redux.getState().countryFilter;

    const shouldFetch =
        (paramsChanges?.duration !== undefined ||
            paramsChanges?.keyword !== undefined ||
            countryFilterRedux.countriesData === null) &&
        params.keyword !== undefined &&
        paramsChanges?.duration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    if (shouldFetch) {
        const filtered = Object.fromEntries(Object.entries(params).filter(([_key, value]) => value));
        const paramsForApi = apiHelper.transformParamsForAPI(filtered, "KeywordAnalysisV2");
        const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(params.keyword);
        const key = isKeywordGroup ? paramsForApi.term.substring(1) : paramsForApi.term;
        paramsForApi.keys = key;
        const queryStringParams = queryString.stringify(paramsForApi);
        try {
            const countriesRank: Record<number, number> = await DefaultFetchService.getInstance().get(
                `/widgetApi/KeywordAnalysisOP/KeywordAnalysisVolumes/TopCountries?${queryStringParams}`,
            );

            const worldwideRank = Object.values(countriesRank).reduce((agr, cur) => agr + cur);
            countriesRank[WORLDWIDE_COUNTRY_ID] = worldwideRank;
            SwRedux.getInstance().dispatch(setCountryFilterData(countriesRank));
        } catch (e) {}
    }
};

const customSegmentsResolver = async () => {
    await loadCustomSegmentsMetadata();
};

const customCategoriesResolver = async () => {
    await loadCustomIndustriesMetadata();
};

export const resolvers = {
    /**
     * @deprecated DO NOT USE THIS RESOLVER
     * it should be used only for the dashboards to prefetch the keyword groups
     * @todo: remove this resolver when we remove dashboard from the app
     */
    dashboard: {
        token: "dashboard",
        deps: ["$transition$"],
        resolveFn: dashboardResolver(),
    },
    siteInfo: {
        token: "siteInfo",
        deps: ["$transition$"],
        resolveFn: siteInfo(),
    },
    appInfo: {
        token: "appInfo",
        deps: ["$transition$"],
        resolveFn: appInfoResolver,
    },
    topCountries: {
        token: "topCounties",
        deps: ["$transition$"],
        resolveFn: topCountriesResolver,
    },
    customSegments: {
        token: "customSegments",
        deps: ["$transition$"],
        resolveFn: () => {
            return customSegmentsResolver();
        },
    },
    customCategories: {
        token: "customCategories",
        deps: ["$transition$"],
        resolveFn: () => {
            return customCategoriesResolver();
        },
    },
};
